import { createFileRoute } from '@tanstack/react-router';
import { useQuery } from '@tanstack/react-query';
import { Download, FileText, RotateCw } from 'lucide-react';
import { Trans, useLingui } from '@lingui/react/macro';
import { msg } from '@lingui/core/macro';
import { mdReportsQueryOptions } from '@/hooks/queries/reports.ts';
import { getBaseUrl } from '@/lib/api/utils.ts';
import { Button } from '@/components/ui/button';
import { Card, CardAction, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { PageContainer } from '@/components/page-container.tsx';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';

export const Route = createFileRoute('/_logged/reports')({
  component: ReportsPage,
  staticData: { label: msg`Accounting Reports` },
});

function downloadUrl(id: number): string {
  return `${getBaseUrl()}/parkadmin/current/mdreports/${id}/download/`;
}

function ReportsPage() {
  const { t } = useLingui();
  const query = useQuery(mdReportsQueryOptions());
  const reports = query.data ?? [];

  return (
    <PageContainer>
      <Card>
        <CardHeader>
          <CardTitle className='flex items-center gap-2'>
            <FileText className='size-5' />
            <Trans>Accounting Reports</Trans>
          </CardTitle>
          <CardAction>
            <Button
              variant='outline'
              onClick={() => void query.refetch()}
              disabled={query.isFetching}
            >
              <RotateCw />
              {query.isFetching ? t`Refreshing…` : t`Refresh`}
            </Button>
          </CardAction>
        </CardHeader>
        <CardContent>
          {query.isPending ?
            <div className='space-y-2'>
              {Array.from({ length: 4 }).map((_, index) => (
                <Skeleton key={index} className='h-10 w-full' />
              ))}
            </div>
          : query.isError ?
            <p className='text-muted-foreground py-6 text-center text-sm'>
              <Trans>Could not load reports.</Trans>
            </p>
          : reports.length === 0 ?
            <p className='text-muted-foreground py-6 text-center text-sm'>
              <Trans>No reports available.</Trans>
            </p>
          : <div className='overflow-hidden rounded-md border **:data-[slot=table-container]:max-h-128 **:data-[slot=table-container]:overflow-y-auto'>
              <Table>
                <TableHeader className='bg-card sticky top-0 z-10 shadow'>
                  <TableRow>
                    <TableHead>
                      <Trans>Society</Trans>
                    </TableHead>
                    <TableHead>
                      <Trans>Start</Trans>
                    </TableHead>
                    <TableHead>
                      <Trans>End</Trans>
                    </TableHead>
                    <TableHead className='text-right'>
                      <Trans>Download</Trans>
                    </TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {reports.map((report) => (
                    <TableRow key={report.id}>
                      <TableCell className='font-medium'>
                        {report.moneydestination?.society_name ?? '—'}
                      </TableCell>
                      <TableCell className='whitespace-nowrap'>{report.start}</TableCell>
                      <TableCell className='whitespace-nowrap'>{report.end}</TableCell>
                      <TableCell className='text-right'>
                        <Button
                          nativeButton={false}
                          render={
                            <a href={downloadUrl(report.id)} target='_blank' rel='noreferrer' />
                          }
                          variant='outline'
                          size='sm'
                        >
                          <Download />
                          <Trans>Download</Trans>
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          }
        </CardContent>
      </Card>
    </PageContainer>
  );
}
